// In-memory store for cross-tenant access grants (ADR-0023 §1).
//
// A grant token is a credential. It lives only in this module's memory:
// never in React state, browser storage, cookies, the URL, query keys or
// error messages. A page reload drops every grant, and the admin steps up
// again.
//
// The store hands out the token only through `tokenFor()`, which the axios
// interceptor calls. Everything React reads (`getSnapshot()`) is token-free.

export interface IssuedGrant {
  grantId: string;
  token: string;
  /** ISO time from the api (database clock). */
  expiresAt: string;
}

/** What the UI may see about a live grant. No token. */
export interface GrantInfo {
  tenantId: string;
  grantId: string;
  /** Local epoch ms when the SPA stops using the grant. */
  expiresAtMs: number;
}

export type GrantEventType = 'issued' | 'expired' | 'ended' | 'rejected' | 'cleared';

export interface GrantEvent {
  type: GrantEventType;
  tenantId: string;
  grantId: string;
}

type Listener = (event: GrantEvent) => void;

interface Entry extends GrantInfo {
  token: string;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * The api's `expiresAt` comes from the database clock, and the browser clock
 * can be a little off. Stop a few seconds early so the SPA never sends a
 * grant the server already treats as expired.
 */
export const EXPIRY_MARGIN_MS = 5_000;

const EMPTY: readonly GrantInfo[] = Object.freeze([]);

export class TenantGrantStore {
  private readonly entries = new Map<string, Entry>();
  private readonly listeners = new Set<Listener>();
  private snapshot: readonly GrantInfo[] = EMPTY;

  /**
   * Stores a new grant. Returns false, and stores nothing, when the grant is
   * already expired by the browser clock.
   */
  set(tenantId: string, grant: IssuedGrant): boolean {
    const key = tenantId.toLowerCase();
    this.drop(key);
    const expiresAtMs = Date.parse(grant.expiresAt) - EXPIRY_MARGIN_MS;
    const delay = expiresAtMs - Date.now();
    if (Number.isNaN(expiresAtMs) || delay <= 0) {
      this.publish();
      return false;
    }
    const timer = setTimeout(() => {
      this.remove(key, 'expired');
    }, delay);
    this.entries.set(key, {
      tenantId: key,
      grantId: grant.grantId,
      expiresAtMs,
      token: grant.token,
      timer,
    });
    this.publish();
    this.emit({ type: 'issued', tenantId: key, grantId: grant.grantId });
    return true;
  }

  /** The live token for this tenant, or null. Only the interceptor calls this. */
  tokenFor(tenantId: string): string | null {
    const entry = this.liveEntry(tenantId);
    if (entry === null) {
      return null;
    }
    return entry.token;
  }

  /** Token-free view of one live grant. */
  get(tenantId: string): GrantInfo | null {
    const entry = this.liveEntry(tenantId);
    if (entry === null) {
      return null;
    }
    return { tenantId: entry.tenantId, grantId: entry.grantId, expiresAtMs: entry.expiresAtMs };
  }

  remove(tenantId: string, reason: Exclude<GrantEventType, 'issued' | 'cleared'>): void {
    const key = tenantId.toLowerCase();
    const entry = this.drop(key);
    if (entry === null) {
      return;
    }
    this.publish();
    this.emit({ type: reason, tenantId: key, grantId: entry.grantId });
  }

  /** Drops every grant. Used on logout and when auth is cleared. */
  clear(): void {
    const dropped: Entry[] = [];
    for (const key of [...this.entries.keys()]) {
      const entry = this.drop(key);
      if (entry !== null) {
        dropped.push(entry);
      }
    }
    if (dropped.length === 0) {
      return;
    }
    this.publish();
    for (const entry of dropped) {
      this.emit({ type: 'cleared', tenantId: entry.tenantId, grantId: entry.grantId });
    }
  }

  /** Stable, token-free list for `useSyncExternalStore`. */
  getSnapshot = (): readonly GrantInfo[] => {
    return this.snapshot;
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private liveEntry(tenantId: string): Entry | null {
    const key = tenantId.toLowerCase();
    const entry = this.entries.get(key);
    if (entry === undefined) {
      return null;
    }
    if (entry.expiresAtMs <= Date.now()) {
      // The timer is late (a throttled background tab). Expire it now.
      this.remove(key, 'expired');
      return null;
    }
    return entry;
  }

  private drop(key: string): Entry | null {
    const entry = this.entries.get(key);
    if (entry === undefined) {
      return null;
    }
    clearTimeout(entry.timer);
    this.entries.delete(key);
    return entry;
  }

  private publish(): void {
    if (this.entries.size === 0) {
      this.snapshot = EMPTY;
      return;
    }
    this.snapshot = Object.freeze(
      [...this.entries.values()].map((entry) => {
        return { tenantId: entry.tenantId, grantId: entry.grantId, expiresAtMs: entry.expiresAtMs };
      }),
    );
  }

  private emit(event: GrantEvent): void {
    for (const listener of [...this.listeners]) {
      listener(event);
    }
  }
}

/** The app's one grant store. */
export const grantStore = new TenantGrantStore();
